import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, CreditCard, Truck, FileText, XCircle, Copyright, AlertTriangle, Tag, Mail } from 'lucide-react';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h1 className="text-4xl font-bold text-[#2d3c5d] mb-4">
            Términos y Condiciones de Uso
          </h1>
          <p className="text-gray-600">
            Al ingresar y utilizar este sitio web, el usuario acepta los siguientes términos de uso, contratación y políticas comerciales. Te recomendamos leerlos antes de realizar una compra.
          </p>
        </div>

        {/* Contenido */}
        <div className="space-y-6">
          {/* 1. Condiciones Generales */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#2d3c5d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-[#2d3c5d]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#2d3c5d] mb-3">1. Condiciones Generales</h2>
                <div className="text-gray-700 space-y-3">
                  <p>
                    Para comprar en <strong>Deco Home Sin Rival SRL</strong> se requerirá brindar datos personales veraces y actualizados (nombre, apellido, correo electrónico, domicilio, etc.).
                  </p>
                  <p>
                    Estos datos se tratan conforme a la <strong>Ley N.º 25.326 de Protección de Datos Personales</strong>, bajo protocolo SSL para resguardar su seguridad.
                  </p>
                  <p>
                    El usuario puede acceder, rectificar o eliminar sus datos escribiendo a{' '}
                    <a href="mailto:info@decohomesinrival.com.ar" className="text-[#2d3c5d] hover:underline font-semibold">
                      info@decohomesinrival.com.ar
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 2. Medios de Pago */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#2d3c5d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-6 h-6 text-[#2d3c5d]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#2d3c5d] mb-3">2. Medios de Pago</h2>
                <div className="text-gray-700 space-y-2">
                  <ul className="list-disc list-inside space-y-1">
                    <li>Los pagos se realizan mediante <strong>Mercado Pago</strong> o <strong>transferencia bancaria</strong>.</li>
                    <li>Todos los pagos están sujetos a verificación y acreditación por parte de la plataforma.</li>
                    <li>Deco Home Sin Rival no se responsabiliza por fallas de los servicios externos de pago.</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* 3. Envíos y Plazos */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#2d3c5d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Truck className="w-6 h-6 text-[#2d3c5d]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#2d3c5d] mb-3">3. Envíos y Plazos de Entrega</h2>
                <div className="text-gray-700 space-y-2">
                  <ul className="list-disc list-inside space-y-1">
                    <li>Realizamos envíos a todo el país a través de <strong>OCA</strong>.</li>
                    <li>Los plazos de entrega comienzan a correr una vez confirmado el pago y suelen ser de <strong>7 a 10 días hábiles</strong> para despacho más el tiempo de transporte (<strong>3 a 5 días hábiles adicionales</strong>).</li>
                    <li>El comprador recibirá por correo electrónico el número de pedido y confirmación de compra.</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* 4. Costos de Envío */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#2d3c5d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Tag className="w-6 h-6 text-[#2d3c5d]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#2d3c5d] mb-3">4. Costos de Envío</h2>
                <div className="text-gray-700 space-y-2">
                  <ul className="list-disc list-inside space-y-1">
                    <li>El costo se calcula automáticamente según peso, destino y monto total y se muestra antes de finalizar la compra.</li>
                    <li>Las promociones con envío gratuito aplican solo a los productos o campañas específicas y no son acumulables con otros descuentos.</li>
                    <li>Nos reservamos el derecho de modificar o cancelar promociones sin previo aviso.</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* 5. Entregas y Recepción */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#2d3c5d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Truck className="w-6 h-6 text-[#2d3c5d]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#2d3c5d] mb-3">5. Entregas y Recepción</h2>
                <div className="text-gray-700 space-y-2">
                  <ul className="list-disc list-inside space-y-1">
                    <li>La entrega se realiza en el domicilio informado por el comprador. Es responsabilidad del usuario verificar que los datos sean correctos.</li>
                    <li>No es posible seleccionar horario exacto de entrega.</li>
                    <li>Actualmente no se realizan retiros por depósito, salvo que se indique expresamente un punto autorizado de retiro.</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* 6. Facturación */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#2d3c5d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-[#2d3c5d]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#2d3c5d] mb-3">6. Facturación</h2>
                <p className="text-gray-700">
                  Las facturas se envían por correo electrónico al finalizar la compra, con el detalle completo de los productos adquiridos.
                </p>
              </div>
            </div>
          </section>

          {/* 7. Cancelación de Órdenes */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#2d3c5d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <XCircle className="w-6 h-6 text-[#2d3c5d]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#2d3c5d] mb-3">7. Cancelación de Órdenes</h2>
                <div className="text-gray-700 space-y-2">
                  <ul className="list-disc list-inside space-y-1">
                    <li>Las órdenes pueden cancelarse solo si no fueron despachadas.</li>
                    <li>El usuario deberá contactarse mediante el botón de arrepentimiento en el sitio.</li>
                    <li>Las solicitudes se responden dentro de las <strong>72 horas hábiles</strong>.</li>
                    <li>Si el pedido ya fue enviado, el costo del reenvío o devolución correrá por cuenta del comprador.</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* 8. Propiedad Intelectual */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#2d3c5d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Copyright className="w-6 h-6 text-[#2d3c5d]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#2d3c5d] mb-3">8. Propiedad Intelectual</h2>
                <div className="text-gray-700 space-y-2">
                  <p>
                    Todo el contenido del sitio —textos, imágenes, logotipos, gráficos y diseños— es propiedad de <strong>Deco Home Sin Rival SRL</strong>.
                  </p>
                  <p className="font-semibold">
                    Queda prohibida su reproducción total o parcial sin autorización.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 9. Política en caso de Sobreventa */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#2d3c5d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-[#2d3c5d]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#2d3c5d] mb-3">9. Política en caso de Sobreventa</h2>
                <div className="text-gray-700 space-y-3">
                  <p>
                    En caso de falta de stock o error de sistema, nos contactaremos dentro de los <strong>7 a 10 días hábiles</strong> para ofrecerte:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Cambio por otro producto de valor similar.</li>
                    <li>Reintegro total del importe abonado.</li>
                    <li>Cupón de crédito para una futura compra.</li>
                  </ul>
                  <p className="font-semibold">Nuestro compromiso es brindarte una solución adecuada.</p>
                </div>
              </div>
            </div>
          </section>

          {/* 10. Promociones y Beneficios */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#2d3c5d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Tag className="w-6 h-6 text-[#2d3c5d]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#2d3c5d] mb-3">10. Promociones y Beneficios</h2>
                <div className="text-gray-700 space-y-2">
                  <ul className="list-disc list-inside space-y-1">
                    <li>
                      Las promociones y descuentos son válidos solo en{' '}
                      <a href="https://decohomesinrival.com.ar" className="text-[#2d3c5d] hover:underline font-semibold">
                        https://decohomesinrival.com.ar
                      </a>
                    </li>
                    <li>No son acumulables con otras ofertas ni aplican a productos de sitios externos.</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Contacto */}
          <section className="bg-[#2d3c5d] rounded-lg p-8 text-white">
            <div className="flex items-start gap-4">
              <Mail className="w-8 h-8 flex-shrink-0" style={{ color: '#eddacb' }} />
              <div>
                <h2 className="text-2xl font-bold mb-4">💌 Contacto</h2>
                <p className="mb-4" style={{ color: '#eddacb' }}>
                  Por consultas o asistencia, escribinos a{' '}
                  <a href="mailto:info@decohomesinrival.com.ar" className="hover:underline font-semibold">
                    info@decohomesinrival.com.ar
                  </a>{' '}
                  o utilizá el botón de ayuda disponible en el sitio.
                </p>
                <a
                  href="mailto:info@decohomesinrival.com.ar"
                  className="inline-flex items-center px-6 py-3 rounded-lg font-semibold transition"
                  style={{
                    backgroundColor: '#eddacb',
                    color: '#2d3c5d',
                  }}
                >
                  Contactar ahora
                </a>
              </div>
            </div>
          </section>

          {/* Botón volver */}
          <div className="pt-6">
            <Link
              to="/"
              className="inline-block px-6 py-3 rounded-lg font-semibold text-white transition"
              style={{ backgroundColor: '#2d3c5d' }}
            >
              Volver al inicio
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;
